import Modal from "@/components/common/modal";
import {
  Button,
  TextField,
  TextAreaField,
  useToast,
  Avatar,
  AvatarFallback,
} from "@elaniintc/goes-design-system";
import { Icon } from "@elaniintc/goes-design-system";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCreateCancelFormalityRequest } from "@/queries/formaly-request";
import {
  createCancelRequestSchema,
  type CancelRequestFormData,
} from "@/schemas/formality-request/cancel-request";

interface CreateCancelRequestModalProps {
  isOpen?: boolean;
  onClose?: () => void;
  requestCode: string;
  requestId: string;
}

export const CreateCancelRequestModal = ({
  isOpen = false,
  onClose = () => {},
  requestCode,
  requestId,
}: CreateCancelRequestModalProps) => {
  const { showToast } = useToast();
  const { mutateAsync: createCancelRequest, isPending } =
    useCreateCancelFormalityRequest();

  const {
    register,
    handleSubmit: handleFormSubmit,
    formState: { errors, isValid },
    reset,
    setValue,
  } = useForm<CancelRequestFormData>({
    mode: "onChange",
    resolver: zodResolver(createCancelRequestSchema(requestCode)),
    defaultValues: {
      requestNumber: "",
      cancellationReason: "",
    },
  });

  const onSubmit = async (data: CancelRequestFormData) => {
    try {
      await createCancelRequest({
        formality_request_id: requestId,
        reason: data.cancellationReason,
      });

      showToast({
        title: "Solicitud de cancelación enviada",
        description: `Has enviado la solicitud de cancelación del trámite ${requestCode}.`,
        options: {
          customIcon: (
            <Avatar size="xs" shape="square">
              <AvatarFallback color="danger">
                <Icon type="fas" name="times" className="text-danger-quiet" />
              </AvatarFallback>
            </Avatar>
          ),
          duration: 5000,
        },
      });

      reset();
      onClose();
    } catch (error: unknown) {
      console.error("Error creating cancel request:", error);
      showToast({
        title: "Error al crear solicitud",
        description:
          "No se pudo crear la solicitud. Por favor, intenta nuevamente.",
        options: {
          type: "error",
          duration: 5000,
        },
      });
    }
  };

  const handleBack = () => {
    reset();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title=""
      modalContentClassName="max-w-[600px]"
    >
      <div className="space-y-6">
        {/* Header Section */}
        <div className="text-center">
          <h1 className="text-center text-h2! font-semibold text-heading leading-[1.3] tracking-[-0.02em]">
            Cancelar solicitud
          </h1>
          <p className="text-body text-base leading-relaxed">
            Al cancelar el trámite{" "}
            <span className="font-semibold">{requestCode}</span> se detendrá por
            completo y todo el proceso que se ha realizado hasta ahora se
            perderá:
          </p>
        </div>

        {/* Form Section */}
        <div className="space-y-2">
          <TextField
            label="Número de solicitud (sin guiones)"
            required
            type="text"
            {...register("requestNumber", {
              onChange: (e) => {
                const upperValue = e.target.value
                  .toUpperCase()
                  .replace(/-/g, "");
                setValue("requestNumber", upperValue, {
                  shouldValidate: true,
                });
              },
            })}
            placeholder="Escribe el número de solicitud"
            className="w-full"
            error={errors.requestNumber?.message}
            onPaste={(e) => e.preventDefault()}
            autoComplete="off"
          />

          <TextAreaField
            label="Motivo de cancelación"
            required
            {...register("cancellationReason")}
            placeholder="Escribe el motivo por el cual estás cancelando la solicitud"
            className="w-full resize-none"
            error={errors.cancellationReason?.message}
          />
        </div>

        {/* Footer Actions */}
        <div className="flex gap-3 pt-4">
          <Button
            type="button"
            variant="tertiary"
            className="flex-1"
            onClick={handleBack}
            disabled={isPending}
          >
            Volver
          </Button>
          <Button
            type="button"
            variant="danger-primary"
            className="flex-1"
            onClick={handleFormSubmit(onSubmit)}
            disabled={!isValid || isPending}
          >
            {isPending ? "Solicitando..." : "Solicitar cancelación"}
            {isPending && (
              <Icon
                type="fas"
                name="spinner-third"
                size="sm"
                className="animate-spin"
              />
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
